import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { clientLog } from 'common/utils/helpers';
import { TRAVERSE_TREE_TARGETS } from 'client/components/common/BuildingBlocks/utils/common';
import { traverseTree } from 'client/components/common/BuildingBlocks/utils/traverse-tree';
import { Context } from 'client/components/common/ResponsiveProvider/Context';
import { BBModel, StoryMediaPlatform, StoryMediaQuery, StorySymbols } from 'types/story';
import { ClientReducerState } from 'client/reducers';
import {
	ITraverseTreeOptions,
	ITraverseTreeOptionsListeners,
} from 'client/components/common/BuildingBlocks/utils/traverse-tree-types';

const log = clientLog.extend('TreeRenderer');
const { Consumer } = Context;
const defaultProps = {
	// eslint-disable-next-line react/default-props-match-prop-types
	cardElements: [],
	symbols: {},
};

const mapStateToProps = (state: ClientReducerState) => ({
	state: state.editor.state,
	cardElements: state.card.elements!,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector> & {
	// state: EditableStateInfo;
	treeOptions:
		| { isCardTree: ITraverseTreeOptions['isCardTree']; target: ITraverseTreeOptions['target'] }
		| ({ isCardTree: ITraverseTreeOptions['isCardTree']; target: 'editor' } & ITraverseTreeOptionsListeners);
	elements: BBModel[];
	symbols?: StorySymbols;
};

const TreeRenderer = (props: Props) => {
	const { elements, treeOptions } = props;
	const isEditor = treeOptions.target === TRAVERSE_TREE_TARGETS.EDITOR;
	const extraProps = isEditor ? { state: props.state } : {};

	log(`%c${treeOptions.isCardTree ? 'cardElements' : 'storyElements'}`, 'font-weight: bold');

	function renderApp(mediaQuery: StoryMediaQuery, currentMediaQuery: StoryMediaPlatform | '') {
		try {
			return traverseTree(elements, {
				...treeOptions,
				...extraProps,
				mediaQuery,
				currentMediaQuery,
				cardElements: props.cardElements,
				symbols: props.symbols,
			});
		} catch (e) {
			if (isEditor) {
				// FIXME
				const l = window?.parent?.document?.documentElement?.querySelector('[class^="editor-loading--"]');
				if (l) {
					if (l.previousSibling) (l.previousSibling as HTMLElement).style.height = '100vh';
					l.remove();
				}
			}
			return null;
		}
	}

	return (
		<Consumer>
			{({ mediaQuery, currentMediaQuery }) =>
				typeof mediaQuery !== 'undefined' &&
				typeof currentMediaQuery !== 'undefined' &&
				renderApp(mediaQuery, currentMediaQuery)
			}
		</Consumer>
	);
};

TreeRenderer.defaultProps = defaultProps;

function areEqual(prevProps, nextProps) {
	const isElementsEqual = prevProps.elements === nextProps.elements;
	const isSymbolsEqual = prevProps.symbols === nextProps.symbols;
	const isStateEqual =
		Object.keys(nextProps.state.source).some(key => nextProps.state.source[key] === prevProps.state.source[key]) &&
		prevProps.state.state === nextProps.state.state;

	if (!nextProps.treeOptions.isCardTree) {
		const isCardElementsEqual = prevProps.cardElements === nextProps.cardElements;
		return isStateEqual && isElementsEqual && isSymbolsEqual && isCardElementsEqual;
	}

	return isStateEqual && isElementsEqual && isSymbolsEqual;
}

export default connector(React.memo(TreeRenderer, areEqual));
